/* ============================================================================
 * Movatic WMS — Frontend | CesarFilho 932048016
 * Arquivo: src/pages/workforce/AbsencesPage.tsx
 * ----------------------------------------------------------------------------
 * Workforce • Ausências / Férias
 *
 * Versão 2 (compat Visual Studio):
 *  - Lista + filtros (período / tipo / estado)
 *  - Botão "Nova ausência" com formulário simples
 *  - Ações rápidas: Aprovar / Rejeitar / Cancelar
 *
 * API:
 *  - GET   /api/workforce/absences/
 *  - POST  /api/workforce/absences/
 *  - PATCH /api/workforce/absences/{id}/
 * ========================================================================== */

import * as React from "react";
import { useEffect, useState, type FormEvent } from "react";
import { useAuthStore } from "../../store/authStore";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../components/ui/table";

/* -------------------------------------------------------------------------- */
/* Tipos e helpers                                                             */
/* -------------------------------------------------------------------------- */

type Absence = {
    id: number;
    operator: number;
    operator_username: string;
    company: number;
    company_code: string;
    absence_type: string;
    start_date: string; // "YYYY-MM-DD"
    end_date: string; // "YYYY-MM-DD"
    reason: string | null;
    status: string;
    created_at: string;
    created_by: number | null;
    approved_by: number | null;
};

type OperatorLite = {
    id: number;
    username: string;
    full_name?: string | null;
};

type FormMode = "hidden" | "create" | "edit";

type AbsenceFormState = {
    id?: number | null;
    operatorId: number | "";
    absenceType: string;
    startDate: string;
    endDate: string;
    reason: string;
    status: string;
};

const ABSENCE_TYPE_LABEL: Record<string, string> = {
    VAC: "Férias",
    SCK: "Doença",
    JST: "Justificada",
    UJT: "Injustificada",
    ACC: "Acidente de trabalho",
    OTH: "Outra",
};

const ABSENCE_STATUS_LABEL: Record<string, string> = {
    REQ: "Solicitada",
    APR: "Aprovada",
    REJ: "Rejeitada",
    CXL: "Cancelada",
};

/**
 * NOTA: aqui evitamos usar import.meta.env por causa do TypeScript do Visual
 * Studio. Para esta tela, /api é suficiente (segue padrão do resto do app).
 */
const API_BASE = "/api";
const WF_BASE = `${API_BASE}/workforce`;

/** Monta headers com Authorization (quando existir token). */
function buildAuthHeaders(contentTypeJson = true): HeadersInit {
    const state: any = (useAuthStore as any)?.getState?.() ?? {};
    const token: string | undefined = state.accessToken;
    const headers: HeadersInit = {};
    if (contentTypeJson) {
        headers["Content-Type"] = "application/json";
    }
    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }
    return headers;
}

/** Retorna [primeiroDiaDoMês, últimoDiaDoMês] em formato YYYY-MM-DD. */
function defaultMonthRange(): { from: string; to: string } {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
        from: from.toISOString().slice(0, 10),
        to: to.toISOString().slice(0, 10),
    };
}

/** Formata data "YYYY-MM-DD" para "dd/MM/yyyy". */
function formatDate(dateStr?: string | null): string {
    if (!dateStr) return "-";
    const [y, m, d] = dateStr.split("-");
    if (!y || !m || !d) return dateStr;
    return `${d}/${m}/${y}`;
}

/** Calcula número de dias entre duas datas (inclusivo). */
function diffDaysInclusive(startStr: string, endStr: string): number | null {
    if (!startStr || !endStr) return null;
    const start = new Date(startStr);
    const end = new Date(endStr);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
    const ms = end.getTime() - start.getTime();
    const days = Math.floor(ms / (1000 * 60 * 60 * 24)) + 1;
    return days > 0 ? days : 1;
}

/* -------------------------------------------------------------------------- */
/* Componente principal                                                        */
/* -------------------------------------------------------------------------- */

export default function AbsencesPage() {
    const { from: defaultFrom, to: defaultTo } = defaultMonthRange();

    const [dateFrom, setDateFrom] = useState<string>(defaultFrom);
    const [dateTo, setDateTo] = useState<string>(defaultTo);
    const [absenceType, setAbsenceType] = useState<string>("");
    const [status, setStatus] = useState<string>("");

    const [items, setItems] = useState<Absence[]>([]);
    const [operators, setOperators] = useState<OperatorLite[]>([]);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [info, setInfo] = useState<string | null>(
        "Em breve: registo de férias, faltas e licenças, com aprovação e impacto no planeamento de mão de obra. Nesta versão já é possível consultar e registar ausências."
    );

    const [formMode, setFormMode] = useState<FormMode>("hidden");
    const [form, setForm] = useState<AbsenceFormState>({
        id: null,
        operatorId: "",
        absenceType: "VAC",
        startDate: defaultFrom,
        endDate: defaultFrom,
        reason: "",
        status: "REQ",
    });

    /* -------------------------- carregar operadores --------------------------- */

    async function loadOperators(): Promise<void> {
        try {
            const url = `${WF_BASE}/operators/?ordering=user__username`;
            const res = await fetch(url, {
                method: "GET",
                headers: buildAuthHeaders(false),
                credentials: "include",
            });
            if (!res.ok) return;
            const data = await res.json();
            let results: OperatorLite[] = [];
            if (Array.isArray(data)) {
                results = data as OperatorLite[];
            } else if (Array.isArray(data?.results)) {
                results = data.results as OperatorLite[];
            }
            setOperators(results);
        } catch {
            // silencioso; não queremos bloquear a página por isto
        }
    }

    /* -------------------------- carregar ausências --------------------------- */

    async function loadAbsences(): Promise<void> {
        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams();
            if (dateFrom) params.append("date_from", dateFrom);
            if (dateTo) params.append("date_to", dateTo);
            if (absenceType) params.append("absence_type", absenceType);
            if (status) params.append("status", status);

            const url = `${WF_BASE}/absences/?${params.toString()}`;
            const res = await fetch(url, {
                method: "GET",
                headers: buildAuthHeaders(false),
                credentials: "include",
            });

            if (!res.ok) {
                const text = await res.text().catch(() => "");
                setError(
                    `Erro ao carregar ausências (HTTP ${res.status}). ${text}`.trim()
                );
                setItems([]);
                setInfo(null);
                return;
            }

            const data = await res.json();

            let results: Absence[] = [];
            if (Array.isArray(data)) {
                results = data as Absence[];
            } else if (Array.isArray(data?.results)) {
                results = data.results as Absence[];
            }

            setItems(results);

            if (results.length === 0) {
                setInfo("Não há ausências registadas para o período/filtros selecionados.");
            } else {
                setInfo(null);
            }
        } catch (e) {
            console.error(e);
            setError("Erro de comunicação com o backend ao carregar ausências.");
            setItems([]);
            setInfo(null);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        void loadAbsences();
        void loadOperators();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateFrom, dateTo, absenceType, status]);

    function handleResetPeriodToThisMonth() {
        const { from, to } = defaultMonthRange();
        setDateFrom(from);
        setDateTo(to);
    }

    /* ----------------------------- formulário -------------------------------- */

    function openCreateForm() {
        const { from } = defaultMonthRange();
        setForm({
            id: null,
            operatorId: "",
            absenceType: "VAC",
            startDate: from,
            endDate: from,
            reason: "",
            status: "REQ",
        });
        setFormMode("create");
        setError(null);
    }

    function openEditForm(abs: Absence) {
        setForm({
            id: abs.id,
            operatorId: abs.operator,
            absenceType: abs.absence_type,
            startDate: abs.start_date,
            endDate: abs.end_date,
            reason: abs.reason ?? "",
            status: abs.status,
        });
        setFormMode("edit");
        setError(null);
    }

    function closeForm() {
        setFormMode("hidden");
    }

    function handleFormChange<K extends keyof AbsenceFormState>(
        field: K,
        value: AbsenceFormState[K]
    ) {
        setForm((prev) => ({
            ...prev,
            [field]: value,
        }));
    }

    async function handleFormSubmit(ev: FormEvent) {
        ev.preventDefault();
        if (!form.operatorId || !form.startDate || !form.endDate) {
            setError("Preencha operador, datas e tipo de ausência.");
            return;
        }

        setSaving(true);
        setError(null);

        try {
            const payload = {
                operator: form.operatorId,
                absence_type: form.absenceType,
                start_date: form.startDate,
                end_date: form.endDate,
                reason: form.reason || null,
                status: form.status || "REQ",
            };

            const isEdit = formMode === "edit" && form.id;
            const url = isEdit
                ? `${WF_BASE}/absences/${form.id}/`
                : `${WF_BASE}/absences/`;
            const method = isEdit ? "PATCH" : "POST";

            const res = await fetch(url, {
                method,
                headers: buildAuthHeaders(true),
                credentials: "include",
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const text = await res.text().catch(() => "");
                setError(
                    `Erro ao ${isEdit ? "guardar" : "criar"} ausência (HTTP ${res.status
                        }). ${text}`.trim()
                );
                return;
            }

            closeForm();
            await loadAbsences();
            setInfo(isEdit ? "Ausência atualizada com sucesso." : "Ausência criada com sucesso.");
        } catch (e) {
            console.error(e);
            setError("Erro de comunicação ao guardar a ausência.");
        } finally {
            setSaving(false);
        }
    }

    /* ------------------------ ações rápidas (status) -------------------------- */

    async function updateAbsenceStatus(abs: Absence, newStatus: string) {
        if (abs.status === newStatus) return;
        setSaving(true);
        setError(null);

        try {
            const url = `${WF_BASE}/absences/${abs.id}/`;
            const res = await fetch(url, {
                method: "PATCH",
                headers: buildAuthHeaders(true),
                credentials: "include",
                body: JSON.stringify({ status: newStatus }),
            });

            if (!res.ok) {
                const text = await res.text().catch(() => "");
                setError(
                    `Erro ao atualizar estado da ausência (HTTP ${res.status}). ${text}`.trim()
                );
                return;
            }

            await loadAbsences();
            setInfo("Estado da ausência atualizado.");
        } catch (e) {
            console.error(e);
            setError("Erro de comunicação ao atualizar estado da ausência.");
        } finally {
            setSaving(false);
        }
    }

    function operatorLabel(id: number): string {
        const op = operators.find((o) => o.id === id);
        if (!op) return `ID ${id}`;
        const full = (op.full_name || "").trim();
        return full || op.username || `ID ${id}`;
    }

    /* -------------------------------- render --------------------------------- */

    return (
        <div className="p-4 lg:px-6 lg:py-6 space-y-6">
            <header className="space-y-1">
                <h1 className="text-2xl font-semibold">Workforce • Ausências / Férias</h1>
                <p className="text-sm text-slate-300">
                    Registo de férias, faltas e licenças, com aprovação e impacto no
                    planeamento de mão de obra. Nesta versão já é possível consultar,
                    criar e atualizar ausências.
                </p>
            </header>

            {/* Mensagens de estado */}
            {error && (
                <div className="rounded-md border border-red-500/60 bg-red-500/10 px-4 py-2 text-sm text-red-100">
                    {error}
                </div>
            )}
            {info && !error && (
                <div className="rounded-md border border-sky-500/60 bg-sky-500/10 px-4 py-2 text-sm text-sky-100">
                    {info}
                </div>
            )}

            {/* Filtros + ações */}
            <section className="space-y-3 rounded-lg border border-slate-800 bg-slate-950/40 px-4 py-3">
                <div className="flex flex-wrap items-end gap-3">
                    <div className="space-y-1">
                        <label className="text-xs font-medium" htmlFor="abs-from">
                            De
                        </label>
                        <Input
                            id="abs-from"
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            className="w-40"
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs font-medium" htmlFor="abs-to">
                            Até
                        </label>
                        <Input
                            id="abs-to"
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            className="w-40"
                        />
                    </div>

                    <div className="space-y-1">
                        <label className="text-xs font-medium" htmlFor="abs-type">
                            Tipo
                        </label>
                        <select
                            id="abs-type"
                            value={absenceType}
                            onChange={(e) => setAbsenceType(e.target.value)}
                            className="w-40 rounded-md border border-slate-700 bg-slate-950/60 px-2 py-1 text-sm"
                        >
                            <option value="">Todos</option>
                            <option value="VAC">Férias</option>
                            <option value="SCK">Doença</option>
                            <option value="JST">Falta justificada</option>
                            <option value="UJT">Falta injustificada</option>
                            <option value="ACC">Acidente de trabalho</option>
                            <option value="OTH">Outra</option>
                        </select>
                    </div>

                    <div className="space-y-1">
                        <label className="text-xs font-medium" htmlFor="abs-status">
                            Estado
                        </label>
                        <select
                            id="abs-status"
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            className="w-40 rounded-md border border-slate-700 bg-slate-950/60 px-2 py-1 text-sm"
                        >
                            <option value="">Todos</option>
                            <option value="REQ">Solicitada</option>
                            <option value="APR">Aprovada</option>
                            <option value="REJ">Rejeitada</option>
                            <option value="CXL">Cancelada</option>
                        </select>
                    </div>

                    <div className="flex items-end gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleResetPeriodToThisMonth}
                        >
                            Este mês
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            onClick={() => void loadAbsences()}
                            disabled={loading}
                        >
                            {loading ? "A atualizar..." : "Atualizar"}
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={openCreateForm}
                            disabled={saving}
                        >
                            Nova ausência
                        </Button>
                    </div>
                </div>
            </section>

            {/* Tabela de ausências */}
            <div className="border border-slate-800 rounded-lg overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-[220px]">Operador</TableHead>
                            <TableHead className="w-[140px]">Tipo</TableHead>
                            <TableHead className="w-[220px]">Período</TableHead>
                            <TableHead className="w-[80px] text-center">Dias</TableHead>
                            <TableHead className="w-[140px]">Estado</TableHead>
                            <TableHead>Motivo</TableHead>
                            <TableHead className="w-[200px] text-right">Ações</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell className="text-sm text-slate-300">
                                    A carregar ausências...
                                </TableCell>
                                <TableCell />
                                <TableCell />
                                <TableCell />
                                <TableCell />
                                <TableCell />
                                <TableCell />
                            </TableRow>
                        ) : items.length === 0 ? (
                            <TableRow>
                                <TableCell className="text-sm text-slate-300">
                                    Nenhuma ausência encontrada para o período/filtros.
                                </TableCell>
                                <TableCell />
                                <TableCell />
                                <TableCell />
                                <TableCell />
                                <TableCell />
                                <TableCell />
                            </TableRow>
                        ) : (
                            items.map((abs) => {
                                const typeLabel =
                                    ABSENCE_TYPE_LABEL[abs.absence_type] ?? abs.absence_type;
                                const statusLabel =
                                    ABSENCE_STATUS_LABEL[abs.status] ?? abs.status;
                                const days = diffDaysInclusive(
                                    abs.start_date,
                                    abs.end_date
                                );

                                return (
                                    <TableRow key={abs.id}>
                                        <TableCell className="text-sm">
                                            <div className="font-medium">
                                                {operatorLabel(abs.operator) ||
                                                    abs.operator_username ||
                                                    `ID ${abs.operator}`}
                                            </div>
                                            <div className="text-xs text-slate-400">
                                                Operador ID: {abs.operator} • Empresa:{" "}
                                                {abs.company_code}
                                            </div>
                                        </TableCell>

                                        <TableCell>
                                            <span className="inline-flex items-center rounded-full border border-sky-500/60 bg-sky-500/10 px-2 py-0.5 text-xs text-sky-100">
                                                {typeLabel}
                                            </span>
                                        </TableCell>

                                        <TableCell className="text-sm">
                                            {formatDate(abs.start_date)} {" → "}{" "}
                                            {formatDate(abs.end_date)}
                                        </TableCell>

                                        <TableCell className="text-center text-sm">
                                            {days ?? "-"}
                                        </TableCell>

                                        <TableCell>
                                            <span className="inline-flex items-center rounded-full border border-emerald-500/60 bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-100">
                                                {statusLabel}
                                            </span>
                                        </TableCell>

                                        <TableCell className="text-sm text-slate-200">
                                            {abs.reason || (
                                                <span className="text-slate-500">—</span>
                                            )}
                                        </TableCell>

                                        <TableCell className="text-right space-x-2">
                                            {abs.status !== "APR" && (
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="outline"
                                                    className="h-7 px-2 text-xs"
                                                    onClick={() => void updateAbsenceStatus(abs, "APR")}
                                                    disabled={saving}
                                                >
                                                    Aprovar
                                                </Button>
                                            )}
                                            {abs.status !== "REJ" && (
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="outline"
                                                    className="h-7 px-2 text-xs"
                                                    onClick={() => void updateAbsenceStatus(abs, "REJ")}
                                                    disabled={saving}
                                                >
                                                    Rejeitar
                                                </Button>
                                            )}
                                            {abs.status !== "CXL" && (
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="outline"
                                                    className="h-7 px-2 text-xs"
                                                    onClick={() => void updateAbsenceStatus(abs, "CXL")}
                                                    disabled={saving}
                                                >
                                                    Cancelar
                                                </Button>
                                            )}
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="outline"
                                                className="h-7 px-2 text-xs"
                                                onClick={() => openEditForm(abs)}
                                                disabled={saving}
                                            >
                                                Editar
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Formulário (inline) */}
            {formMode !== "hidden" && (
                <section className="rounded-lg border border-slate-800 bg-slate-950/60 px-4 py-4 space-y-4">
                    <header className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold">
                            {formMode === "create" ? "Nova ausência" : "Editar ausência"}
                        </h2>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={closeForm}
                            disabled={saving}
                        >
                            Fechar
                        </Button>
                    </header>

                    <form
                        className="grid gap-4 md:grid-cols-2 lg:grid-cols-3"
                        onSubmit={handleFormSubmit}
                    >
                        <div className="space-y-1">
                            <label className="text-xs font-medium" htmlFor="abs-form-operator">
                                Operador
                            </label>
                            <select
                                id="abs-form-operator"
                                value={form.operatorId}
                                onChange={(e) =>
                                    handleFormChange(
                                        "operatorId",
                                        e.target.value ? Number(e.target.value) : ""
                                    )
                                }
                                className="w-full rounded-md border border-slate-700 bg-slate-950/60 px-2 py-1 text-sm"
                            >
                                <option value="">Selecione...</option>
                                {operators.map((op) => (
                                    <option key={op.id} value={op.id}>
                                        {operatorLabel(op.id)}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="space-y-1">
                            <label className="text-xs font-medium" htmlFor="abs-form-type">
                                Tipo
                            </label>
                            <select
                                id="abs-form-type"
                                value={form.absenceType}
                                onChange={(e) =>
                                    handleFormChange("absenceType", e.target.value)
                                }
                                className="w-full rounded-md border border-slate-700 bg-slate-950/60 px-2 py-1 text-sm"
                            >
                                <option value="VAC">Férias</option>
                                <option value="SCK">Doença</option>
                                <option value="JST">Falta justificada</option>
                                <option value="UJT">Falta injustificada</option>
                                <option value="ACC">Acidente de trabalho</option>
                                <option value="OTH">Outra</option>
                            </select>
                        </div>

                        <div className="space-y-1">
                            <label className="text-xs font-medium" htmlFor="abs-form-status">
                                Estado
                            </label>
                            <select
                                id="abs-form-status"
                                value={form.status}
                                onChange={(e) => handleFormChange("status", e.target.value)}
                                className="w-full rounded-md border border-slate-700 bg-slate-950/60 px-2 py-1 text-sm"
                            >
                                <option value="REQ">Solicitada</option>
                                <option value="APR">Aprovada</option>
                                <option value="REJ">Rejeitada</option>
                                <option value="CXL">Cancelada</option>
                            </select>
                        </div>

                        <div className="space-y-1">
                            <label className="text-xs font-medium" htmlFor="abs-form-start">
                                Início
                            </label>
                            <Input
                                id="abs-form-start"
                                type="date"
                                value={form.startDate}
                                onChange={(e) => handleFormChange("startDate", e.target.value)}
                            />
                        </div>

                        <div className="space-y-1">
                            <label className="text-xs font-medium" htmlFor="abs-form-end">
                                Fim
                            </label>
                            <Input
                                id="abs-form-end"
                                type="date"
                                value={form.endDate}
                                onChange={(e) => handleFormChange("endDate", e.target.value)}
                            />
                        </div>

                        <div className="space-y-1 md:col-span-2 lg:col-span-3">
                            <label className="text-xs font-medium" htmlFor="abs-form-reason">
                                Motivo / Observações
                            </label>
                            <textarea
                                id="abs-form-reason"
                                value={form.reason}
                                onChange={(e) => handleFormChange("reason", e.target.value)}
                                className="w-full min-h-[70px] rounded-md border border-slate-700 bg-slate-950/60 px-2 py-1 text-sm"
                            />
                        </div>

                        <div className="md:col-span-2 lg:col-span-3 flex justify-end gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={closeForm}
                                disabled={saving}
                            >
                                Cancelar
                            </Button>
                            <Button type="submit" size="sm" disabled={saving}>
                                {saving
                                    ? formMode === "create"
                                        ? "A criar..."
                                        : "A guardar..."
                                    : formMode === "create"
                                        ? "Criar ausência"
                                        : "Guardar alterações"}
                            </Button>
                        </div>
                    </form>
                </section>
            )}
        </div>
    );
}
